import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const getAgeLabel = (value) => {
  const map = {
    A00: "All Ages (14-99)",
    A01: "14-18",
    A02: "19-21",
    A03: "22-24",
    A04: "25-34",
    A05: "35-44",
    A06: "45-54",
    A07: "55-64",
    A08: "65-99",
  };
  return map[value] || "Unknown";
};

const SpecificAgeGroupEmployeeChart = ({ data }) => {
  const pdfRef = useRef();
  const safeData = data ?? [];

  if (!safeData.length) {
    return <p>No data available</p>;
  }

  const years = Object.keys(safeData[0]).filter((key) =>
    key.startsWith("year_"),
  );

  // =========================
  // Summary Calculations
  // =========================
  const totalAllYears = safeData.reduce((sum, row) => {
    return (
      sum +
      years.reduce((rowSum, year) => rowSum + (parseInt(row[year]) || 0), 0)
    );
  }, 0);

  const latestYear = years[years.length - 1];

  const topAgeGroupLatest = safeData.reduce(
    (max, row) =>
      parseInt(row[latestYear] || 0) > parseInt(max[latestYear] || 0)
        ? row
        : max,
    safeData[0],
  );

  // =========================
  // Chart Data
  // =========================
  const colorPalette = [
    "#014CB2",
    "#CB0C0C",
    "#22c55e",
    "#f59e0b",
    "#8b5cf6",
    "#10b981",
    "#EC4899",
    "#6366f1",
  ];

  const chartData = {
    labels: years.map((year) => year.replace("year_", "")),
    datasets: safeData.map((item, index) => ({
      label: getAgeLabel(item.agegrp_label),
      data: years.map((year) => parseInt(item[year] || 0)),
      backgroundColor: colorPalette[index % colorPalette.length],
      borderRadius: 4,
      borderWidth: 0,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Stacked Employee Distribution by Age Group (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: "Year" },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Employees",
        },
        ticks: {
          callback: (value) => value.toLocaleString(),
        },
      },
    },
  };

  // =========================
  // PDF Export
  // =========================
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);

      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("specific_age_group_analysis.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Specific Age Group Employee Data
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-600">Latest Year</p>
            <p className="text-xl font-bold text-blue-800">
              {latestYear.replace("year_", "")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-600">
              Total Employees (All Years)
            </p>
            <p className="text-xl font-bold text-green-800">
              {totalAllYears.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-600">
              Top Age Group ({latestYear.replace("year_", "")})
            </p>
            <p className="text-xl font-bold text-yellow-800">
              {getAgeLabel(topAgeGroupLatest.agegrp_label)}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  {years.map((year) => (
                    <th key={year} className="p-2">
                      {year.replace("year_", "")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {safeData.map((row) => (
                  <tr key={row.agegrp_label} className="hover:bg-gray-100">
                    <td className="p-2">{getAgeLabel(row.agegrp_label)}</td>
                    {years.map((year) => (
                      <td key={year} className="p-2">
                        {Number(row[year] || 0).toLocaleString()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4 h-96">
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf" />
        </button>
      </div>
    </>
  );
};

export default SpecificAgeGroupEmployeeChart;
